/**
 * Content-Hub web adapter configuration.
 *
 * The adapter exposes two endpoints:
 *  - POST {BASE}/v1/search  — historical events (initial list data)
 *  - GET  {BASE}/v1/stream  — one shared SSE connection for all sources
 *
 * All endpoints are protected. The HTTP calls (POST /v1/search, POST
 * /v1/stream/configure, POST /v1/stream/pass) carry the user's Keycloak bearer
 * token via the shared `contentHubClient` (see ./client.ts). The SSE stream
 * can't send headers, so opening it is a two-step exchange: POST /v1/stream/pass
 * mints a short-lived, single-use pass that GET /v1/stream then accepts as a
 * query param (see fetchStreamPassUrl + the stream manager's connect flow).
 */
export const CONTENT_HUB_ADAPTER_URL: string =
  import.meta.env.VITE_CONTENT_HUB_ADAPTER_URL ?? "";

/** Join an endpoint path onto the adapter base URL, preserving any sub-path
 *  in the base (e.g. `http://ip/sub/path/` + `v1/stream` →
 *  `http://ip/sub/path/v1/stream`). `new URL(path, base)` would drop the
 *  base path for leading-slash paths, so join strings instead. */
export const contentHubUrl = (path: string): string =>
  `${CONTENT_HUB_ADAPTER_URL.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

/** Sources the frontend currently has views/adapters for. Adding a source
 *  here is all the data-layer wiring a new source needs — the stream manager
 *  routes any backend_source generically to its per-source query. */
export const CONTENT_HUB_SOURCES = {
  HEADLINES: "newsquawk-headlines",
  TRUTH_SOCIAL: "truth-social",
  FIN_X: "fin-x",
  /** Energy X (Twitter) */
  ENERGY_X: "energy-x",
  /** The Fly */
  STOCKS_ONE: "stocks-one",
  /** Benzinga */
  STOCKS_TWO: "stocks-two",
  /** Benzinga */
  STOCKS_THREE: "stocks-three",
  /** TradeXchange */
  STOCKS_FOUR: "stocks-four",
  /** TradeXchange filings */
  STOCKS_FOUR_FILINGS: "stocks-four-filings",
  /** TradeXchange fed speak */
  STOCKS_FOUR_FEDSPEAK: "stocks-four-fedspeak",
  /** TradeXchange general */
  STOCKS_FOUR_GENERAL: "stocks-four-general",
  /** Crypto blogs & websites scraper */
  CRYPTO_WEB: "crypto-web-news",
  /** General web scraper */
  FIN_WEB: "fin-web-news",
  /** Energy blogs & websites scraper */
  ENERGY_WEB: "energy-web-news",
  /** SEC filings */
  SEC_FILINGS: "newsquawk-sec-filings",
  /** Newsquawk economic calendar events (data/earning/supply/speaker/etc.) —
   *  rendered by the calendar-v2 feature's grouped grid, not the news lists. */
  CALENDAR_EVENT: "newsquawk-calendar-event",
  /** Financial Reports EU */
  FINANCIAL_REPORTS_EU: "stocks-five-eu",
  // ── Benzinga structured equities feeds (datahose) ──────────────────────────
  // Structured market-data events (not news stories). Rendered by the
  // equities-widgets table views, which map each item's raw payload through
  // their own transformer. Their rich column search still runs on the
  // dedicated /equities-data/* endpoints; content-hub supplies the live stream
  // + the unfiltered initial history.
  /** Congressional / government trades */
  GOVERNMENT_TRADE: "government-trade",
  /** Unusual option activity */
  OPTION_ACTIVITY: "option-activity",
  /** Insider (Form 4) transactions */
  INSIDER_TRANSACTION: "insider-transaction",
  /** Block trades */
  BLOCK_TRADE: "block-trade",
  // ── 13F (secapi) ───────────────────────────────────────────────────────────
  // One secapi filing is split into two grains, queried independently via
  // typed f13_ columns. The payload is the raw secapi object.
  /** One doc per 13F filing (accession) — the filings list */
  SEC_13F_FILING: "newsquawk-sec-13f-filing",
  /** One doc per position (accession:cusip:put_or_call) — holdings/screening */
  SEC_13F_HOLDING: "newsquawk-sec-13f-holding",
} as const;

export type ContentHubSource =
  (typeof CONTENT_HUB_SOURCES)[keyof typeof CONTENT_HUB_SOURCES];

/** Every declared content-hub source id, for membership checks. */
export const CONTENT_HUB_SOURCE_VALUES = Object.values(
  CONTENT_HUB_SOURCES
) as ContentHubSource[];

/** Type guard: is this string a source the content-hub layer handles? Use it to
 *  scope a dynamic source list (e.g. the firehose-all aggregate, built from the
 *  legacy source registry) down to real content-hub sources — excluding synthetic
 *  aggregates like "stocks-news" and any source not yet integrated here. */
export const isContentHubSource = (value: string): value is ContentHubSource =>
  (CONTENT_HUB_SOURCE_VALUES as readonly string[]).includes(value);

/** Named source lists for combined views. A combined widget is just a
 *  composition over these — see useContentHubFeed. */
export const CONTENT_HUB_SOURCE_GROUPS = {
  /** Equities news combined view — The Fly, Benzinga & TradeXchange */
  STOCKS_NEWS: [
    CONTENT_HUB_SOURCES.STOCKS_ONE,
    CONTENT_HUB_SOURCES.STOCKS_TWO,
    CONTENT_HUB_SOURCES.STOCKS_THREE,
    CONTENT_HUB_SOURCES.STOCKS_FOUR,
    CONTENT_HUB_SOURCES.STOCKS_FOUR_FILINGS,
  ],
  /** Trade Xchange combined view — all four TradeXchange sub-feeds */
  TRADE_XCHANGE: [
    CONTENT_HUB_SOURCES.STOCKS_FOUR,
    CONTENT_HUB_SOURCES.STOCKS_FOUR_FEDSPEAK,
    CONTENT_HUB_SOURCES.STOCKS_FOUR_FILINGS,
    CONTENT_HUB_SOURCES.STOCKS_FOUR_GENERAL,
  ],
} as const;

/** Page size for each /v1/search fetch (cursor pages of this size). */
export const SEARCH_PAGE_SIZE = 100;

/** Cap on the first page's size after live-stream prepends, so a burst of
 *  live events can't grow page one without bound. */
export const MAX_FIRST_PAGE = SEARCH_PAGE_SIZE * 3;

/** Fallback delay before a filtered query is allowed to fetch page one even if
 *  the stream `configure` hasn't acknowledged — so a slow or configure-less
 *  adapter degrades to non-live search instead of hanging. */
export const CONFIGURE_READY_TIMEOUT_MS = 3_000;

/** Debounce for coalescing rapid filter register/unregister churn into one
 *  /v1/stream/configure call. */
export const CONFIGURE_DEBOUNCE_MS = 300;

/** Root query key for all content-hub event queries. */
export const CONTENT_HUB_EVENTS_KEY = ["content-hub", "events"] as const;

/** Query key for a base-feed query over one or more sources. The source list
 *  is sorted so equivalent source sets share a cache entry, and lives in the
 *  key so the stream manager can patch any query that contains a given source. */
export const contentHubEventsKey = (sources: readonly ContentHubSource[]) =>
  [...CONTENT_HUB_EVENTS_KEY, [...sources].sort()] as const;

/** Root query key for the public 24h-delayed demo feed. Kept distinct from the
 *  live events key so the stream manager never touches (or invalidates) it —
 *  the delayed feed has no live stream. */
export const CONTENT_HUB_DELAYED_KEY = ["content-hub", "delayed"] as const;

/** Query key for a delayed base-feed query over one or more sources. */
export const contentHubDelayedKey = (sources: readonly ContentHubSource[]) =>
  [...CONTENT_HUB_DELAYED_KEY, [...sources].sort()] as const;

/** Root query key for filtered content-hub search queries. Kept distinct from
 *  CONTENT_HUB_EVENTS_KEY so the stream manager (which invalidates events)
 *  never touches filtered caches. */
export const CONTENT_HUB_SEARCH_KEY = ["content-hub", "search"] as const;

/** Query key for a filtered search: distinct per pinned source + normalized
 *  filter (equivalent filters share a key). */
export const contentHubSearchKey = (
  pinnedSource: ContentHubSource | null,
  normalizedFilter: Record<string, unknown>
) => [...CONTENT_HUB_SEARCH_KEY, pinnedSource, normalizedFilter] as const;
